import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Archive as ArchiveIcon, CaretRight, CaretDown, Scissors, ArrowSquareOut, ClipboardText, Truck, Package, Scroll, Wrench } from "@phosphor-icons/react";

const TYPE_ICON = { film: Scroll, tools: Wrench, other: Package };

function describeItem(it) {
  if (it.order_type === "other") return it.item_name || "—";
  return [it.manufacturer_name, it.series_name, it.product_name].filter(Boolean).join(" · ") || it.item_name || "—";
}
function qtyLabel(it) {
  if (it.unit === "meters") return `${it.quantity}m`;
  if (it.unit === "units") return `${it.quantity} units`;
  return `${it.quantity} rolls${it.length_per_roll_m ? ` × ${it.length_per_roll_m}m` : ""}`;
}
function fmtDate(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); }
  catch { return d; }
}

export default function Archive() {
  const [tab, setTab] = useState("rolls");
  const [rolls, setRolls] = useState([]);
  const [products, setProducts] = useState({});
  const [mfrs, setMfrs] = useState({});
  const [series, setSeries] = useState({});
  const [orders, setOrders] = useState({ order_items: [], purchase_orders: [] });
  const [loading, setLoading] = useState(true);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [cuts, setCuts] = useState({});
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [{ data: r }, { data: p }, { data: m }, { data: s }] = await Promise.all([
        api.get("/rolls", { params: { archived: true } }),
        api.get("/products"), api.get("/manufacturers"), api.get("/series"),
      ]);
      setRolls(r);
      setProducts(Object.fromEntries(p.map((x) => [x.id, x])));
      setMfrs(Object.fromEntries(m.map((x) => [x.id, x])));
      setSeries(Object.fromEntries(s.map((x) => [x.id, x])));
    } catch (e) {
      setError("Could not load the archive. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const loadOrders = async () => {
    setOrdersLoading(true);
    try {
      const { data } = await api.get("/archived-orders");
      setOrders(data);
    } catch (e) { /* handled by empty state */ }
    finally { setOrdersLoading(false); }
  };

  useEffect(() => { load(); loadOrders(); }, []);

  const toggle = async (roll) => {
    if (expanded === roll.id) { setExpanded(null); return; }
    setExpanded(roll.id);
    if (!cuts[roll.id]) {
      const { data } = await api.get(`/rolls/${roll.id}/cuts`);
      setCuts((prev) => ({ ...prev, [roll.id]: data }));
    }
  };

  const describe = (roll) => {
    const p = products[roll.product_id];
    if (!p) return roll.sku;
    return [mfrs[p.manufacturer_id]?.name, series[p.series_id]?.name, p.name].filter(Boolean).join(" · ");
  };

  const orderCount = orders.order_items.length + orders.purchase_orders.length;

  return (
    <div className="p-8 max-w-[1100px]" data-testid="archive-page">
      <header className="mb-6">
        <div className="text-xs font-mono tracking-[0.2em] uppercase text-slate-500 mb-2">Insights</div>
        <h1 className="font-heading text-4xl font-bold tracking-tight text-slate-900 flex items-center gap-3">
          <ArchiveIcon size={32} /> Archive
        </h1>
        <p className="text-sm text-slate-600 mt-2">A kept record of completed rolls and past orders. Orders older than 30 days move here automatically so your active tabs stay tidy.</p>
      </header>

      {/* Tabs */}
      <div className="flex items-center gap-2 mb-6 border-b border-slate-200">
        <button
          onClick={() => setTab("rolls")}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === "rolls" ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}
          data-testid="archive-tab-rolls"
        >
          Depleted Rolls <span className="font-mono text-xs text-slate-400">({rolls.length})</span>
        </button>
        <button
          onClick={() => setTab("orders")}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === "orders" ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}
          data-testid="archive-tab-orders"
        >
          Previous Orders <span className="font-mono text-xs text-slate-400">({orderCount})</span>
        </button>
      </div>

      {/* DEPLETED ROLLS */}
      {tab === "rolls" && (
        loading ? (
          <div className="text-sm text-slate-500">Loading…</div>
        ) : error ? (
          <div className="border border-red-200 bg-red-50 p-6 text-center rounded-md" data-testid="archive-error">
            <div className="text-sm text-red-700">{error}</div>
            <button onClick={load} className="mt-3 text-sm font-medium text-red-800 underline" data-testid="archive-retry">Retry</button>
          </div>
        ) : rolls.length === 0 ? (
          <div className="border border-dashed border-slate-300 bg-white p-12 text-center rounded-md">
            <ArchiveIcon size={30} className="mx-auto text-slate-300 mb-3" />
            <div className="text-sm text-slate-500">No completed rolls yet. Rolls move here automatically once fully cut.</div>
          </div>
        ) : (
          <div className="space-y-2" data-testid="archive-rolls-list">
            {rolls.map((roll) => {
              const isOpen = expanded === roll.id;
              const rollCuts = cuts[roll.id] || [];
              return (
                <div key={roll.id} className="border border-slate-200 bg-white rounded-md overflow-hidden" data-testid={`archive-roll-${roll.id}`}>
                  <button onClick={() => toggle(roll)} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50" data-testid={`archive-toggle-${roll.id}`}>
                    {isOpen ? <CaretDown size={16} className="text-slate-400 shrink-0" /> : <CaretRight size={16} className="text-slate-400 shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-900 truncate">{describe(roll)}</div>
                      <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap gap-x-4 gap-y-0.5 items-center">
                        <span><span className="text-slate-400">Batch</span> <span className="font-mono">{roll.batch_number || "—"}</span></span>
                        <span><span className="text-slate-400">Expires</span> <span className="font-mono">{roll.expiry_date || "—"}</span></span>
                        {roll.allocated_order && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-mono tracking-[0.1em] uppercase px-1.5 py-0.5 rounded-sm bg-violet-50 text-violet-700 border border-violet-200" data-testid={`archive-order-${roll.id}`}>
                            <ClipboardText size={11} /> Order · {roll.allocated_order}
                          </span>
                        )}
                        {roll.finish_reason && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-mono tracking-[0.1em] uppercase px-1.5 py-0.5 rounded-sm bg-slate-100 text-slate-600 border border-slate-200" data-testid={`archive-finish-${roll.id}`}>
                            {roll.finish_reason}
                          </span>
                        )}
                      </div>
                    </div>
                    <Link to={`/rolls/${roll.id}`} onClick={(e) => e.stopPropagation()} className="text-slate-400 hover:text-slate-800 shrink-0" data-testid={`archive-open-${roll.id}`} title="Open full detail">
                      <ArrowSquareOut size={16} />
                    </Link>
                  </button>

                  {isOpen && (
                    <div className="border-t border-slate-100 px-4 py-3 bg-slate-50/50">
                      {roll.allocated_order && (
                        <div className="mb-3 flex items-center gap-2 text-sm text-violet-800 bg-violet-50 border border-violet-200 rounded-md px-3 py-2" data-testid={`archive-order-detail-${roll.id}`}>
                          <ClipboardText size={16} weight="fill" />
                          <span>Consumed on arrival — allocated to order <span className="font-semibold">{roll.allocated_order}</span></span>
                        </div>
                      )}
                      <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500 mb-2 flex items-center gap-1.5">
                        <Scissors size={12} /> Cut history
                      </div>
                      {rollCuts.length === 0 ? (
                        <div className="text-sm text-slate-500 py-2">{roll.allocated_order ? "No cuts — this roll was allocated to an order on arrival." : "No cuts were recorded for this roll."}</div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-left text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500">
                                <th className="py-2 pr-4">When</th>
                                <th className="py-2 pr-4 text-right">Length</th>
                                <th className="py-2 pr-4">Customer</th>
                                <th className="py-2">By</th>
                              </tr>
                            </thead>
                            <tbody>
                              {rollCuts.map((c) => (
                                <tr key={c.id} className="border-t border-slate-100">
                                  <td className="py-2 pr-4 font-mono text-xs">{new Date(c.cut_at).toLocaleString()}</td>
                                  <td className="py-2 pr-4 font-mono text-right tabular-nums font-semibold">−{c.length_m.toFixed(2)}m</td>
                                  <td className="py-2 pr-4">{c.customer || "—"}</td>
                                  <td className="py-2 text-slate-500 text-xs">{(c.user_email || "").split("@")[0]}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      )}

      {/* PREVIOUS ORDERS */}
      {tab === "orders" && (
        ordersLoading ? (
          <div className="text-sm text-slate-500">Loading…</div>
        ) : orderCount === 0 ? (
          <div className="border border-dashed border-slate-300 bg-white p-12 text-center rounded-md" data-testid="previous-orders-empty">
            <ClipboardText size={30} className="mx-auto text-slate-300 mb-3" />
            <div className="text-sm text-slate-500">No previous orders yet. Ordered items and received shipments move here after 30 days.</div>
          </div>
        ) : (
          <div className="space-y-8" data-testid="previous-orders-section">
            {/* Received shipments (purchase orders) */}
            {orders.purchase_orders.length > 0 && (
              <section>
                <h2 className="font-heading text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <Truck size={18} /> Received Shipments <span className="text-slate-400 font-mono text-sm">({orders.purchase_orders.length})</span>
                </h2>
                <div className="border border-slate-200 bg-white rounded-md divide-y divide-slate-100">
                  {orders.purchase_orders.map((po) => (
                    <div key={po.id} className="px-4 py-3 flex items-center gap-4" data-testid={`archived-po-${po.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-slate-900 truncate">{po.reference || "Shipment"}</div>
                        <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap gap-x-4 gap-y-0.5">
                          {po.supplier && <span><span className="text-slate-400">Supplier</span> {po.supplier}</span>}
                          <span><span className="text-slate-400">Received</span> {fmtDate(po.received_at)}</span>
                          <span><span className="text-slate-400">Items</span> {po.item_count}</span>
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="font-mono text-sm tabular-nums">{po.total_rolls} rolls · {po.total_meters}m</div>
                        <div className="text-[10px] text-slate-400">archived {fmtDate(po.archived_at)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Order-list items */}
            {orders.order_items.length > 0 && (
              <section>
                <h2 className="font-heading text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <ClipboardText size={18} /> Order List <span className="text-slate-400 font-mono text-sm">({orders.order_items.length})</span>
                </h2>
                <div className="border border-slate-200 bg-white rounded-md divide-y divide-slate-100">
                  {orders.order_items.map((it) => {
                    const Icon = TYPE_ICON[it.order_type] || Package;
                    return (
                      <div key={it.id} className="px-4 py-3 flex items-center gap-4" data-testid={`archived-oi-${it.id}`}>
                        <Icon size={16} className="text-slate-400 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-slate-900 truncate flex items-center gap-2">
                            <span className="truncate">{describeItem(it)}</span>
                            {it.base_sku && <span className="text-[10px] font-mono text-slate-500 bg-slate-100 border border-slate-200 rounded-sm px-1.5 py-0.5 shrink-0">{it.base_sku}</span>}
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5">
                            {qtyLabel(it)}{it.order_reference ? ` · order ${it.order_reference}` : ""}{it.note ? ` · ${it.note}` : ""}
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="font-mono text-xs text-slate-500">ordered {fmtDate(it.ordered_at)}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}
          </div>
        )
      )}
    </div>
  );
}
